/**
 * AlertTitleInput — the corporate dashboard's single-line mini WYSIWYG for an
 * account alert's title (corp-dashboard/components/title-rich-input.tsx).
 * Bold / italic / underline and colour apply to the current selection (see
 * execTitleCommand), so the value is inline HTML — always passed through
 * sanitizeTitleHtml, which reduces it to <b>/<i>/<u> and hex-colour <span>s.
 *
 * The element is uncontrolled while focused (writing innerHTML on every
 * keystroke would throw the caret to the start); outside values are applied
 * only when the field isn't being typed in, and the DOM is normalised to the
 * sanitized value on blur.
 *
 * Inside a locked FieldLockScope (a Locked Admin form) it shows the title and
 * takes no typing or paste (not contentEditable, aria-readonly), without
 * looking disabled.
 */
import { useEffect, type MutableRefObject } from 'react';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { cn } from '@/lib/utils';
import { sanitizeTitleHtml } from '@/lib/alertHeroStyle';

export function AlertTitleInput({
  id,
  value,
  onChange,
  placeholder,
  invalid = false,
  innerRef,
}: {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  invalid?: boolean;
  innerRef: MutableRefObject<HTMLDivElement | null>;
}) {
  const locked = useFieldLocked();
  useEffect(() => {
    const el = innerRef.current;
    if (!el || document.activeElement === el) return;
    // Rewriting innerHTML wipes the selection; only do it for a value that came
    // from outside — if the DOM already sanitizes to it, the field produced it.
    if (sanitizeTitleHtml(el.innerHTML) === value) return;
    el.innerHTML = value;
  }, [value, innerRef]);

  const emit = () => onChange(sanitizeTitleHtml(innerRef.current?.innerHTML ?? ''));

  return (
    <div
      id={id}
      ref={innerRef}
      contentEditable={!locked}
      role="textbox"
      aria-label="Alert title"
      aria-multiline="false"
      aria-readonly={locked || undefined}
      aria-invalid={(invalid && !locked) || undefined}
      data-placeholder={locked ? undefined : placeholder}
      suppressContentEditableWarning
      onInput={locked ? undefined : emit}
      onBlur={() => {
        const el = innerRef.current;
        if (el && sanitizeTitleHtml(el.innerHTML) !== value) el.innerHTML = value;
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault(); // single line
      }}
      onPaste={(e) => {
        // Paste as plain text so foreign markup never enters the field.
        e.preventDefault();
        if (!locked) document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
      }}
      className={cn(
        'form-input-base min-h-9 w-full',
        'empty:before:pointer-events-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)]',
        invalid && !locked && 'border-red-400 ring-1 ring-red-200',
      )}
    />
  );
}

/**
 * The last selection made inside each title field. A native colour input opens
 * an OS dialog and takes focus with it, so by the time a colour arrives the
 * live selection is gone — this is what puts the highlight back.
 */
const savedRanges = new WeakMap<HTMLElement, Range>();

/** Stash the current selection if it sits inside this field. */
export function rememberTitleSelection(el: HTMLElement | null): void {
  if (!el) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  if (el.contains(range.commonAncestorContainer)) savedRanges.set(el, range.cloneRange());
}

/**
 * Run a formatting command against the title field. The selection is restored
 * first when focus has moved away (the colour picker), falling back to the
 * whole title when there is nothing stashed — so Bold with nothing selected
 * bolds everything rather than silently doing nothing. Toolbar buttons should
 * preventDefault on mousedown to keep focus here.
 */
export function execTitleCommand(el: HTMLDivElement | null, command: 'bold' | 'italic' | 'underline' | 'foreColor', value?: string): void {
  if (!el) return;
  const selection = window.getSelection();
  const inField = !!selection && selection.rangeCount > 0 && el.contains(selection.getRangeAt(0).commonAncestorContainer);
  if (!inField) {
    el.focus();
    const saved = savedRanges.get(el);
    const range = document.createRange();
    if (saved && el.contains(saved.commonAncestorContainer)) {
      range.setStart(saved.startContainer, saved.startOffset);
      range.setEnd(saved.endContainer, saved.endOffset);
    } else {
      range.selectNodeContents(el);
    }
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
  // Colour as an inline-style span, but bold / italic / underline as real
  // <b>/<i>/<u> tags, which is what the sanitizer keeps canonical.
  document.execCommand('styleWithCSS', false, command === 'foreColor' ? 'true' : 'false');
  document.execCommand(command, false, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  rememberTitleSelection(el);
}
